// Phase 36 Batch T — Admin Analytics > Funnel tab.
//
// 5-stage acquisition funnel with conversion rates between stages.
// Visual: horizontal-bar funnel where each bar's width is normalized
// to the top-of-funnel ("signed_up") count. Premium chrome.

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Filter, AlertTriangle, TrendingDown, ArrowRight } from "lucide-react";

const STAGE_LABELS = {
  signed_up: "Signed up",
  created_first_deal: "Created first deal",
  created_3_plus_deals: "Created 3+ deals",
  downloaded_report: "Downloaded a report",
  has_subscription_paid: "On a paid plan",
};

export default function AdminAnalyticsFunnel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/admin/analytics/funnel?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  // Top-of-funnel count, used to normalize each bar's width.
  const top = useMemo(() => {
    if (!data?.stages?.length) return 0;
    return data.stages[0].count || 0;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-analytics-funnel">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-indigo-600">Analytics · Funnel</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            Activation &amp; conversion funnel
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Signed up → first deal → 3+ deals → downloaded report → paid plan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Window</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32" data-testid="funnel-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {err && (
        <Card className="flex items-center gap-3 border-rose-200 bg-rose-50/60 p-4 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4" /> {err}
        </Card>
      )}

      {loading || !data ? (
        <Card className="flex h-72 items-center justify-center text-slate-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading funnel…
        </Card>
      ) : (
        <Card className="p-6">
          <div className="mb-4 flex items-center gap-2 text-slate-900">
            <Filter className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold">Conversion funnel · {days} days</h2>
          </div>
          {top === 0 ? (
            <p className="py-12 text-center text-sm text-slate-500">
              No signups in the selected window. Try a wider range.
            </p>
          ) : (
            <ol className="space-y-3">
              {data.stages.map((s, i) => {
                const pct = top > 0 ? (100 * s.count) / top : 0;
                const conv = s.conversion_from_prev_pct;
                return (
                  <li key={s.stage} data-testid={`funnel-stage-${s.stage}`}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-slate-800">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                          {i + 1}
                        </span>
                        <span className="font-medium">{STAGE_LABELS[s.stage] || s.stage}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-900 tabular-nums">{s.count.toLocaleString()}</span>
                        <span className="w-14 text-right text-xs tabular-nums text-slate-500">
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 h-3 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-indigo-400 transition-all"
                        style={{ width: `${Math.max(2, pct)}%` }}
                      />
                    </div>
                    {i < data.stages.length - 1 && (
                      <div className="ml-7 mt-1 flex items-center gap-1 text-xs text-slate-500">
                        <ArrowRight className="h-3 w-3" />
                        <span>
                          {data.stages[i + 1].conversion_from_prev_pct !== null
                            ? <>
                                <span className="tabular-nums">{data.stages[i + 1].conversion_from_prev_pct}%</span>{" "}
                                convert to next stage
                              </>
                            : "—"}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          <p className="mt-6 flex items-center gap-2 text-xs text-slate-500">
            <TrendingDown className="h-3.5 w-3.5" />
            Each stage is a strict subset of the previous — downstream counts are
            never greater than upstream.
          </p>
        </Card>
      )}
    </div>
  );
}
